import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorProvider } from './contexts/ErrorContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ErrorNotification from './components/ErrorBoundary/ErrorNotification';
import { aiContentService } from './services/aiContentService';
import './styles/theme.css';
import './styles/studio-design-system.css';
import './styles/layout-improvements.css';
import Header from './components/Layout/Header';
import CollapsibleSidebar from './components/Layout/CollapsibleSidebar';
import MobileNavigation from './components/Layout/MobileNavigation';
import MobileBottomNav from './components/Layout/MobileBottomNav';
import ResponsiveContainer from './components/Layout/ResponsiveContainer';
import PreviewModal from './components/Posts/PreviewModal';
import ArticlesList from './components/Public/ArticlesList';
import ArticlePage from './components/Public/ArticlePage';
import LandingPage from './components/Landing/LandingPage';
import ReaderTestPage from './components/Reader/ReaderTestPage';
import ConnectionStatus from './components/Debug/ConnectionStatus';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CreateExperience from './components/Create/CreateExperience';
import SmartCreateAssistant from './components/Create/SmartCreateAssistant';
import { saveOnboardingData } from './services/onboardingService';
import { Post, Subscriber, Analytics as AnalyticsType, ViewType } from './types';
import { getPosts, createPost, updatePost, getSubscribers, getAnalytics, getPostById } from './services/firebaseService';
import { LoadingSpinner } from './components/Loading/LoadingStates';
import PerformanceMonitor from './utils/performanceMonitor';
import SkipLink from './components/Accessibility/SkipLink';
import DarkModeTest from './components/Theme/DarkModeTest';
import SimpleTestRunner from './pages/SimpleTestRunner';
import TestAutomationAPI from './pages/TestAutomationAPI';
import APITestDashboard from './pages/APITestDashboard';
import { shouldShowTestingFeatures, shouldShowDebugFeatures, isProduction } from './utils/environment';
import StudioHub from './components/Studio/StudioHub';

// Lazy load heavy components
import {
  LazyDashboard,
  LazyContentHub,
  LazyAudienceHub,
  LazyReaderHub,
  LazyAnalyticsHub,
  LazyUnifiedAnalyticsHub,
  LazyUnifiedContentHub,
  LazyUnifiedSettingsHub,
  LazyAutomationHub,
  LazyBrandHub,
  LazyAccountHub,
  LazyPostEditor,
  LazyMediaStudioDashboard,
  LazyMigrationPage,
  LazyOnboardingWizard,
  LazyComprehensiveTestingPage
} from './utils/lazyImports';

const MainApp: React.FC = () => {
  const { currentUser } = useAuth();
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [posts, setPosts] = useState<Post[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsType | null>(null);
  
  // Initialize performance monitoring
  useEffect(() => {
    const monitor = PerformanceMonitor.getInstance();
    monitor.startMonitoring();
    
    return () => {
      monitor.stopMonitoring();
    };
  }, []);
  const [editorContent, setEditorContent] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [postExcerpt, setPostExcerpt] = useState('');
  const [postTags, setPostTags] = useState('');
  const [postFeaturedImage, setPostFeaturedImage] = useState('');
  const [postAllowComments, setPostAllowComments] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showCreateExperience, setShowCreateExperience] = useState(false);
  const [showSmartAssistant, setShowSmartAssistant] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Check if user needs onboarding
  useEffect(() => {
    if (!currentUser) return;
    
    const checkOnboardingStatus = async () => {
      try {
        // For now, use localStorage as primary method
        const completed = localStorage.getItem(`onboarding_completed_${currentUser.uid}`);
        if (!completed) {
          setShowOnboarding(true);
          setHasCompletedOnboarding(false);
        } else {
          setShowOnboarding(false);
          setHasCompletedOnboarding(true);
        }
        setOnboardingChecked(true);
        
        // TODO: Implement Firebase onboarding check
        // const completed = await hasCompletedOnboarding(currentUser.uid);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setShowOnboarding(true);
        setHasCompletedOnboarding(false);
        setOnboardingChecked(true);
      }
    };
    
    checkOnboardingStatus();
  }, [currentUser]);

  // Load data from Firebase
  useEffect(() => {
    if (!currentUser) return;

    // If onboarding is not completed, don't load data yet
    if (!hasCompletedOnboarding) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [postsData, subscribersData, analyticsData] = await Promise.all([
          getPosts(currentUser.uid),
          getSubscribers(currentUser.uid),
          getAnalytics(currentUser.uid)
        ]);
        
        setPosts(postsData);
        setSubscribers(subscribersData);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, hasCompletedOnboarding]);

  // Default analytics if none exists
  // const _stats: AnalyticsType = analytics || {
  //   totalSubscribers: subscribers.length,
  //   totalPosts: posts.length,
  //   totalRevenue: 0,
  //   avgOpenRate: 0,
  //   clickRate: 0,
  //   unsubscribeRate: 0,
  //   growthRate: 0,
  // };

  // const _handleCreatePost = () => {
  //   setEditingPost(null);
  //   setPostTitle('');
  //   setEditorContent('');
  //   setPostExcerpt('');
  //   setPostTags('');
  //   setPostFeaturedImage('');
  //   setPostAllowComments(true);
  //   setCurrentView('editor');
  // };

  const handleShowCreateExperience = () => {
    setShowCreateExperience(true);
  };

  const handleStartCreating = async (type: string, template?: any) => {
    setShowCreateExperience(false);
    
    // Reset editor state
    setEditingPost(null);
    setPostTitle('');
    setEditorContent('');
    setPostExcerpt('');
    setPostTags('');
    setPostFeaturedImage('');
    setPostAllowComments(true);
    
    // Handle different content types
    switch (type) {
      case 'newsletter':
        await handleNewsletterCreation(template);
        break;
      case 'article':
        await handleArticleCreation(template);
        break;
      case 'series':
        await handleSeriesCreation(template);
        break;
      case 'announcement':
        await handleAnnouncementCreation(template);
        break;
      case 'poll':
        await handlePollCreation(template);
        break;
      case 'event':
        await handleEventCreation(template);
        break;
      case 'media':
        await handleMediaCreation(template);
        break;
      case 'ai-generated':
        await handleAICreation(template);
        break;
      default:
        // Fallback to basic post creation
        setCurrentView('editor');
    }
  };

  // const _handleShowSmartAssistant = () => {
  //   setShowSmartAssistant(true);
  // };

  const handleSuggestionSelect = (suggestion: any) => {
    setShowSmartAssistant(false);
    // Use the suggestion to pre-populate the editor
    setPostTitle(suggestion.title);
    setPostTags(suggestion.tags.join(', '));
    setCurrentView('editor');
  };

  // Content type handlers
  const handleNewsletterCreation = async (template?: any) => {
    if (template) {
      // Load template content
      const templateContent = await loadTemplate(template.id);
      setPostTitle(templateContent.title);
      setEditorContent(templateContent.content);
      setPostExcerpt(templateContent.excerpt);
      setPostTags(templateContent.tags?.join(', ') || 'newsletter');
    } else {
      // Default newsletter setup
      setPostTitle('Weekly Newsletter');
      setEditorContent(`
        <h2>Welcome to this week's newsletter!</h2>
        <p>Here's what we're covering this week:</p>
        <ul>
          <li>Key updates and news</li>
          <li>Featured content</li>
          <li>Community highlights</li>
          <li>Upcoming events</li>
        </ul>
        <p>Thank you for being part of our community!</p>
      `);
      setPostTags('newsletter, weekly, update');
    }
    setCurrentView('editor');
  };

  const handleArticleCreation = async (template?: any) => {
    if (template) {
      const templateContent = await loadTemplate(template.id);
      setPostTitle(templateContent.title);
      setEditorContent(templateContent.content);
      setPostExcerpt(templateContent.excerpt);
      setPostTags(templateContent.tags?.join(', ') || 'article');
    } else {
      setPostTitle('New Article');
      setEditorContent(`
        <h1>Your Article Title</h1>
        <p>Start writing your article here...</p>
        <h2>Key Points</h2>
        <ul>
          <li>Point 1</li>
          <li>Point 2</li>
          <li>Point 3</li>
        </ul>
        <h2>Conclusion</h2>
        <p>Wrap up your thoughts here.</p>
      `);
      setPostTags('article, blog, content');
    }
    setCurrentView('editor');
  };

  const handleSeriesCreation = async (template?: any) => {
    if (template) {
      const templateContent = await loadTemplate(template.id);
      setPostTitle(`${templateContent.title} - Part 1`);
      setEditorContent(templateContent.content);
      setPostExcerpt(templateContent.excerpt);
      setPostTags(`${templateContent.tags?.join(', ') || 'series'}, part-1`);
    } else {
      setPostTitle('Series: [Series Name] - Part 1');
      setEditorContent(`
        <h1>Welcome to [Series Name]</h1>
        <p>This is the first part of our multi-part series. In this series, we'll cover:</p>
        <ul>
          <li>Part 1: Introduction and overview (this post)</li>
          <li>Part 2: [Topic 1]</li>
          <li>Part 3: [Topic 2]</li>
          <li>Part 4: [Topic 3]</li>
        </ul>
        <h2>Let's get started!</h2>
        <p>Begin your series content here...</p>
      `);
      setPostTags('series, multi-part, tutorial');
    }
    setCurrentView('editor');
  };

  const handleAnnouncementCreation = async (template?: any) => {
    setPostTitle('Important Announcement');
    setEditorContent(`
      <div className="announcement-container">
        <h2 className="announcement-title">📢 Important Announcement</h2>
        <p className="announcement-text">Share your important news here...</p>
      </div>
      <p>This announcement will be sent to all subscribers immediately.</p>
    `);
    setPostTags('announcement, urgent, news');
    setCurrentView('editor');
  };

  const handlePollCreation = async (template?: any) => {
    setPostTitle('Poll: [Your Question]');
    setEditorContent(`
      <h2>What do you think?</h2>
      <p>We'd love to hear your opinion on this topic.</p>
      
      <div className="info-container">
        <h3>Poll Question</h3>
        <p>Add your poll question here and let your audience vote!</p>
        <!-- Poll widget would be inserted here -->
      </div>
      
      <p>Vote and let us know your thoughts in the comments below!</p>
    `);
    setPostTags('poll, survey, engagement, interactive');
    setCurrentView('editor');
  };

  const handleEventCreation = async (template?: any) => {
    setPostTitle('Event: [Event Name]');
    setEditorContent(`
      <div className="event-container">
        <h2 className="event-title">🎉 [Event Name]</h2>
        <div className="event-grid">
          <div><strong>📅 Date:</strong> [Event Date]</div>
          <div><strong>⏰ Time:</strong> [Event Time]</div>
          <div><strong>📍 Location:</strong> [Event Location]</div>
          <div><strong>🎫 RSVP:</strong> [RSVP Link]</div>
        </div>
      </div>
      
      <h3>About This Event</h3>
      <p>Describe your event here...</p>
      
      <h3>What to Expect</h3>
      <ul>
        <li>Key highlights</li>
        <li>Special guests</li>
        <li>Activities</li>
      </ul>
    `);
    setPostTags('event, announcement, rsvp, community');
    setCurrentView('editor');
  };

  const handleMediaCreation = async (template?: any) => {
    setPostTitle('Media Content: [Title]');
    setEditorContent(`
      <h2>Media Content</h2>
      <p>Create engaging media content with audio, video, or visuals.</p>
      
      <div className="news-container">
        <h3>🎥 Video Content</h3>
        <p>Upload your video or embed from YouTube/Vimeo</p>
        <!-- Video upload/embed widget would go here -->
      </div>
      
      <div className="success-container">
        <h3>🎵 Audio Content</h3>
        <p>Record audio or upload audio files</p>
        <!-- Audio recording/upload widget would go here -->
      </div>
      
      <h3>Description</h3>
      <p>Add a description of your media content here...</p>
    `);
    setPostTags('media, video, audio, multimedia, content');
    setCurrentView('editor');
  };

  const handleAICreation = async (template?: any) => {
    try {
      if (template?.type === 'voice-to-text') {
        // Mock audio transcription - in a real app, you'd capture audio first
        const transcribedContent = await aiContentService.transcribeAudio(new Blob());
        setPostTitle('Voice-to-Text Content');
        setEditorContent(`
          <h2>Voice-to-Text Content</h2>
          <p>Your spoken content has been transcribed below. Edit as needed:</p>
          ${transcribedContent}
        `);
        setPostTags('voice-to-text, ai-generated, transcription');
      } else if (template?.type === 'rewrite') {
        // Mock content improvement
        const originalContent = '<p>Original content to be improved...</p>';
        const improvements = ['Better structure', 'Clearer language', 'More engaging tone'];
        const improvedContent = await aiContentService.improveContent(originalContent, improvements);
        setPostTitle('AI-Improved Content');
        setEditorContent(improvedContent);
        setPostTags('ai-generated, improved, rewrite');
      } else {
        // Generate AI content based on type
        const aiRequest = {
          type: template?.contentType || 'article',
          topic: template?.topic || 'AI-generated content',
          tone: template?.tone || 'professional',
          length: template?.length || 'medium',
          audience: template?.audience || 'general audience',
          keywords: template?.keywords || []
        };
        
        const aiResponse = await aiContentService.generateContent(aiRequest);
        setPostTitle(aiResponse.title);
        setEditorContent(aiResponse.content);
        setPostExcerpt(aiResponse.excerpt);
        setPostTags(aiResponse.tags.join(', '));
      }
    } catch (error) {
      console.error('Error in AI creation:', error);
      // Fallback to basic content
      setPostTitle('AI-Generated Content');
      setEditorContent(`
        <h2>AI-Generated Content</h2>
        <p>Let AI help you create amazing content. Start with a topic or idea:</p>
        <div className="highlight-container">
          <p><em>[AI-generated content will appear here based on your prompt]</em></p>
        </div>
        <p>Edit and customize the AI-generated content to match your voice and style.</p>
      `);
      setPostTags('ai-generated, artificial-intelligence, content');
    }
    setCurrentView('editor');
  };

  // Template loading function
  const loadTemplate = async (templateId: string) => {
    // Mock template data - in a real app, this would fetch from a database
    const templates: { [key: string]: any } = {
      'weekly-update': {
        title: 'Weekly Update',
        content: `
          <h2>This Week's Highlights</h2>
          <p>Here's what happened this week in our community:</p>
          <ul>
            <li>Key updates and announcements</li>
            <li>Featured content and resources</li>
            <li>Community highlights and achievements</li>
            <li>Upcoming events and opportunities</li>
          </ul>
          <h2>What's Next</h2>
          <p>Looking ahead to next week...</p>
        `,
        excerpt: 'Weekly community update and highlights',
        tags: ['newsletter', 'weekly', 'update', 'community']
      },
      'announcement': {
        title: 'Important Announcement',
        content: `
          <div className="announcement-container">
            <h2 className="announcement-title">📢 Important Announcement</h2>
            <p className="announcement-text">Share your important news here...</p>
          </div>
        `,
        excerpt: 'Important announcement for our community',
        tags: ['announcement', 'urgent', 'news']
      },
      'how-to': {
        title: 'How-to Guide: [Topic]',
        content: `
          <h1>How to [Topic]</h1>
          <p>A step-by-step guide to help you [achieve your goal].</p>
          
          <h2>What You'll Need</h2>
          <ul>
            <li>Item 1</li>
            <li>Item 2</li>
            <li>Item 3</li>
          </ul>
          
          <h2>Step-by-Step Instructions</h2>
          <ol>
            <li><strong>Step 1:</strong> Description of first step</li>
            <li><strong>Step 2:</strong> Description of second step</li>
            <li><strong>Step 3:</strong> Description of third step</li>
          </ol>
          
          <h2>Tips and Tricks</h2>
          <p>Additional helpful information...</p>
        `,
        excerpt: 'Step-by-step guide to help you achieve your goals',
        tags: ['how-to', 'tutorial', 'guide', 'step-by-step']
      },
      'opinion': {
        title: 'My Take: [Topic]',
        content: `
          <h1>My Take: [Topic]</h1>
          <p>Here's my perspective on [topic] and why it matters.</p>
          
          <h2>The Current State</h2>
          <p>What's happening right now...</p>
          
          <h2>Why This Matters</h2>
          <p>Why this topic is important and relevant...</p>
          
          <h2>My Perspective</h2>
          <p>My personal thoughts and opinions...</p>
          
          <h2>What's Next</h2>
          <p>Looking ahead and what we should consider...</p>
        `,
        excerpt: 'Personal perspective on trending topics',
        tags: ['opinion', 'perspective', 'analysis', 'commentary']
      },
      'tutorial-series': {
        title: 'Tutorial Series: [Topic] - Part 1',
        content: `
          <h1>Welcome to [Series Name]</h1>
          <p>This is the first part of our comprehensive tutorial series.</p>
          
          <h2>Series Overview</h2>
          <p>In this series, we'll cover:</p>
          <ul>
            <li>Part 1: Introduction and setup (this post)</li>
            <li>Part 2: [Next topic]</li>
            <li>Part 3: [Following topic]</li>
            <li>Part 4: [Final topic]</li>
          </ul>
          
          <h2>Getting Started</h2>
          <p>Let's begin with the basics...</p>
        `,
        excerpt: 'First part of our comprehensive tutorial series',
        tags: ['series', 'tutorial', 'multi-part', 'education']
      }
    };
    
    return templates[templateId] || {
      title: 'Template',
      content: '<p>Template content</p>',
      excerpt: 'Template excerpt',
      tags: ['template']
    };
  };

  const handleSavePost = async (status: 'draft' | 'published') => {
    if (!currentUser) return;

    try {
        const postData = {
          title: postTitle,
          content: editorContent,
          status,
          opens: 0,
          clicks: 0,
          publishDate: status === 'published' ? new Date().toISOString().split('T')[0] : null,
          authorId: currentUser.uid,
          authorName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
          authorEmail: currentUser.email || '',
          excerpt: postExcerpt,
          tags: postTags.split(',').map(tag => tag.trim()).filter(tag => tag),
          featuredImage: postFeaturedImage,
          allowComments: postAllowComments,
          likes: 0,
          shares: 0,
          views: 0,
        };

      if (editingPost) {
        // Update existing post
        await updatePost(editingPost.id, postData);
        const updatedPost = { ...editingPost, ...postData };
        setPosts(posts.map(p => p.id === editingPost.id ? updatedPost : p));
      } else {
        // Create new post
        const postId = await createPost(postData);
        const newPost: Post = {
          ...postData,
          id: postId,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setPosts([newPost, ...posts]);
      }

      setCurrentView('posts');
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleViewChange = (view: ViewType) => {
    console.log(`🔄 Navigation: Changing view from ${currentView} to ${view}`);
    setCurrentView(view);
  };

  // Expose navigation function globally for testing
  useEffect(() => {
    (window as any).navigateToView = (view: ViewType) => {
      console.log(`🧪 Test navigation: Setting currentView to ${view}`);
      setCurrentView(view);
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleOnboardingComplete = async (onboardingData: any) => {
    try {
      // Store onboarding data in Firebase
      if (currentUser) {
        await saveOnboardingData(currentUser.uid, {
          ...onboardingData,
          completedSteps: ['welcome', 'audience', 'goals', 'theme']
        });
        
        // Also store in localStorage as backup
        localStorage.setItem(`onboarding_completed_${currentUser.uid}`, 'true');
        localStorage.setItem(`onboarding_data_${currentUser.uid}`, JSON.stringify(onboardingData));
      }
      
      setShowOnboarding(false);
      setHasCompletedOnboarding(true);
      setLoading(false);
      // Redirect to content creation
      setCurrentView('editor');
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      // Fallback to localStorage only
      if (currentUser) {
        localStorage.setItem(`onboarding_completed_${currentUser.uid}`, 'true');
        localStorage.setItem(`onboarding_data_${currentUser.uid}`, JSON.stringify(onboardingData));
      }
      setShowOnboarding(false);
      setHasCompletedOnboarding(true);
      setLoading(false);
      setCurrentView('editor');
    }
  };

  const handleOnboardingSkip = () => {
    if (currentUser) {
      localStorage.setItem(`onboarding_completed_${currentUser.uid}`, 'true');
    }
    setShowOnboarding(false);
    setHasCompletedOnboarding(true);
    setLoading(false); // Ensure loading is false
  };

  const renderCurrentView = () => {
    // Show loading while checking onboarding status
    if (!onboardingChecked) {
      return (
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Setting up your account...</p>
          </div>
        </div>
      );
    }

    // Show onboarding if needed
    if (showOnboarding) {
      return (
        <Suspense fallback={<LoadingSpinner message="Loading Onboarding..." />}>
          <LazyOnboardingWizard
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        </Suspense>
      );
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your data...</p>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'dashboard':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Dashboard..." />}>
            <LazyDashboard
              onCreatePost={handleShowCreateExperience}
              onViewChange={handleViewChange}
            />
          </Suspense>
        );
      
      // Content Hub
      case 'content':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Content Hub..." />}>
            <LazyUnifiedContentHub userId={currentUser?.uid || ''} onNavigate={handleViewChange} />
          </Suspense>
        );
      
      // Audience Hub
      case 'audience':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Audience Hub..." />}>
            <LazyAudienceHub userId={currentUser?.uid || ''} />
          </Suspense>
        );

      // Analytics Hub
      case 'insights':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Analytics Hub..." />}>
            <LazyUnifiedAnalyticsHub userId={currentUser?.uid || ''} />
          </Suspense>
        );

      // Automation Hub
      case 'automation':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Automation Hub..." />}>
            <LazyAutomationHub userId={currentUser?.uid || ''} />
          </Suspense>
        );

      // Brand Hub
      case 'brand':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Brand Hub..." />}>
            <LazyBrandHub userId={currentUser?.uid || ''} />
          </Suspense>
        );

      // Account Hub
      case 'account':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Account Hub..." />}>
            <LazyUnifiedSettingsHub userId={currentUser?.uid || ''} />
          </Suspense>
        );
      
      // Reader Dashboard
      case 'reader':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Reader Hub..." />}>
            <LazyReaderHub userId={currentUser?.uid} />
          </Suspense>
        );
      
      // Reader Test Page (only in development)
      case 'reader-test':
        if (!shouldShowTestingFeatures()) {
          return <div className="p-6 text-center">This feature is not available in production.</div>;
        }
        return <ReaderTestPage />;
      
      // Media Studio
      case 'media-studio':
        console.log('🎬 Rendering Studio Hub');
        return (
          <StudioHub userId={currentUser?.uid || ''} onNavigate={handleViewChange} />
        );
      
      // Comprehensive Testing (only in development)
      case 'testing':
        if (!shouldShowTestingFeatures()) {
          return <div className="p-6 text-center">This feature is not available in production.</div>;
        }
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Testing Dashboard..." />}>
            <LazyComprehensiveTestingPage />
          </Suspense>
        );
      
      case 'editor':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Editor..." />}>
            <LazyPostEditor
              post={editingPost}
              title={postTitle}
              content={editorContent}
              onTitleChange={setPostTitle}
              onContentChange={setEditorContent}
              onSave={handleSavePost}
              onCancel={() => setCurrentView('posts')}
              userId={currentUser?.uid}
            />
          </Suspense>
        );
      
      // Legacy cases for backward compatibility

      default:
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Page Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400">The requested page could not be found on PostSub.io.</p>
          </div>
        );
    }
  };

  return (
    <ResponsiveContainer
      currentView={currentView}
      onViewChange={handleViewChange}
    >
      <PreviewModal
        post={previewPost}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
      
      {/* Mobile Navigation */}
      <MobileNavigation
        currentView={currentView}
        onViewChange={handleViewChange}
      />
      
      {/* Desktop Header */}
      <Header />
      
      <div className="flex min-h-screen">
        {/* Collapsible Sidebar */}
        <CollapsibleSidebar
          id="navigation"
          currentView={currentView}
          onViewChange={handleViewChange}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
        />
        
        {/* Skip Links for Accessibility */}
        <SkipLink targetId="main-content" />
        <SkipLink targetId="navigation" children="Skip to navigation" />
        
        {/* Main Content Area */}
        <main 
          id="main-content"
          className={`
            flex-1 transition-all duration-300 ease-in-out
            ${isSidebarCollapsed ? 'lg:ml-0' : 'lg:ml-0'}
            ${currentView === 'editor' ? 'min-h-screen' : 'min-h-screen'}
            safe-area-pl safe-area-pr
          `}
          role="main"
          aria-label="Main content"
        >
          <div className={`
            ${currentView === 'editor' ? 'min-h-screen' : 'min-h-screen pb-20 lg:pb-6'} 
            ${currentView === 'editor' ? 'p-0' : 'p-4 lg:p-6'}
            safe-area-pt
          `}>
            {renderCurrentView()}
          </div>
        </main>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentView={currentView}
        onViewChange={handleViewChange}
        onCreatePost={handleShowCreateExperience}
      />

      {/* Create Experience Modal */}
      {showCreateExperience && (
        <CreateExperience
          onStartCreating={handleStartCreating}
          onClose={() => setShowCreateExperience(false)}
        />
      )}

      {/* Smart Create Assistant Modal */}
      {showSmartAssistant && (
        <SmartCreateAssistant
          onSuggestionSelect={handleSuggestionSelect}
          onClose={() => setShowSmartAssistant(false)}
        />
      )}
    </ResponsiveContainer>
  );
};

// Simple article page component without nested routing
const SimpleArticlePage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      if (postId) {
        try {
          const postData = await getPostById(postId);
          setPost(postData);
        } catch (error) {
          console.error('Error loading post:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Article Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400">The article you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return <ArticlePage post={post} onLoadPost={async () => {}} />;
};

const App: React.FC = () => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900" data-testid="loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
    </div>
  );
}

  return (
    <Router>
      <Routes>
        {/* Public routes - accessible to everyone */}
        <Route path="/article/:postId" element={<SimpleArticlePage />} />
        <Route path="/articles" element={<ArticlesList />} />
        <Route path="/newsletters" element={<ArticlesList />} />
        <Route path="/migration" element={
          <Suspense fallback={<LoadingSpinner message="Loading Migration..." />}>
            <LazyMigrationPage />
          </Suspense>
        } />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentSuccessPage />} />
        {shouldShowDebugFeatures() && <Route path="/dark-mode-test" element={<DarkModeTest />} />}
        {shouldShowTestingFeatures() && <Route path="/test-runner" element={<SimpleTestRunner />} />}
        {shouldShowTestingFeatures() && <Route path="/test-api" element={<TestAutomationAPI />} />}
        {shouldShowTestingFeatures() && <Route path="/api-tests" element={<APITestDashboard />} />}
        
        {/* Home page and other routes */}
        {currentUser ? (
          <Route path="/*" element={<MainApp />} />
        ) : (
          <Route path="/*" element={<LandingPage />} />
        )}
      </Routes>
      {shouldShowDebugFeatures() && <ConnectionStatus />}
    </Router>
  );
};

const AppWithAuth: React.FC = () => (
  <ThemeProvider>
    <ErrorProvider>
      <AuthProvider>
        <NotificationProvider>
          <ErrorBoundary>
            <App />
            <ErrorNotification />
          </ErrorBoundary>
        </NotificationProvider>
      </AuthProvider>
    </ErrorProvider>
  </ThemeProvider>
);

export default AppWithAuth;